import { Link, usePage } from "@inertiajs/react";
import { ArrowRight, Check, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/format";

// Card colours, in tier order. The tiers themselves (names, student
// ranges, prices) come from CampaignPlan::TIERS via the `priceList` prop,
// the same list the app enforces.
const TONES = [
  { tone: "bg-primary/5 border-primary/20", ink: "text-primary" },
  { tone: "bg-accent/5 border-accent/25", ink: "text-accent" },
  { tone: "bg-warning/5 border-warning/25", ink: "text-warning-foreground" },
];

const INCLUDED = [
  "Student donation pages",
  "Reading logs for home and class",
  "Prize tracking and certificates",
  "Printable QR take-home sheets",
  "Live campaign reports",
  "Hands-on onboarding",
];

export default function PricingTiers({ priceList }) {
  const { routes } = usePage().props;

  return (
    <div className="max-w-5xl mx-auto space-y-7">
      <div className="text-center space-y-3">
        <h2 className="text-3xl md:text-4xl leading-tight">Choose your campaign size.</h2>
        <p className="max-w-2xl mx-auto text-muted-foreground leading-relaxed">
          Your fee is based on students in the campaign, not your school’s
          total enrolment. Every tier includes the full PagePledge app.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3" aria-label="Campaign pricing tiers">
        {priceList.map((tier, i) => (
          <div key={tier.name} className={`flex flex-col rounded-2xl border p-6 ${TONES[i % TONES.length].tone}`}>
            <div className={`flex items-center gap-2 text-sm font-semibold ${TONES[i % TONES.length].ink}`}>
              <Users className="h-4 w-4" aria-hidden="true" />
              <h3>{tier.name} campaign</h3>
            </div>
            <p className="mt-3 text-base font-medium">{tier.students}</p>
            <div className="mt-6">
              <span className="inline-flex rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                Early adopter · {Math.round(100 - (tier.price_cents / tier.standard_price_cents) * 100)}% off
              </span>
              <p className="mt-4 text-lg text-muted-foreground tabular-nums">
                <span className="sr-only">Standard price: </span>
                <del>{formatMoney(tier.standard_price_cents)}</del>
              </p>
              <p className="mt-1 font-display text-5xl tracking-tight tabular-nums">
                <span className="sr-only">Early adopter price: </span>
                {formatMoney(tier.price_cents)}
              </p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">CAD per campaign</p>
            <div className="my-6 border-t border-border/70" />
            <p className="text-sm leading-relaxed text-muted-foreground">
              All features included. Your campaign fee stays the same as donations grow.
            </p>
            <Button asChild variant="outline" className="mt-6 w-full bg-card/80">
              <Link href={routes.new_school_application}>Get started <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-border/70 bg-card/70 p-5 md:p-6">
        <p className="font-semibold">Included in every campaign</p>
        <ul className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          {INCLUDED.map((feature) => (
            <li key={feature} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" /><span>{feature}</span></li>
          ))}
        </ul>
      </div>
      <p className="text-sm text-center text-muted-foreground leading-relaxed max-w-3xl mx-auto">
        Start with a free trial to set up your campaign. Running it costs the flat fee above, paid before it goes live. No PagePledge transaction fees.
        Square or Stripe processing fees are separate, and donors can choose to cover them. Prices shown
        include the early adopter discount.
      </p>
    </div>
  );
}
